<template>
  <div v-if="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status"></div>
    <p class="mt-2 text-muted">Préparation du paiement...</p>
  </div>

  <div v-else>
    <div v-if="paymentStatus === PaymentStatusEnum.Succeeded" class="alert alert-success d-flex align-items-center gap-2">
      <i class="fa-solid fa-circle-check fa-lg"></i>
      <span>Le paiement de {{ formatPriceWithoutPlusSign(amount) }} a bien été effectué.<br>Votre inscription est désormais finalisée.</span>
    </div>

    <template v-else>
      <!-- <div v-if="amount != null" class="alert alert-primary">
        <div class="d-flex justify-content-between align-items-center gap-3">
          <span>Montant à régler</span>
          <strong>{{ formatPriceWithoutPlusSign(amount) }}</strong>
        </div>
      </div> -->

      <div v-if="error" class="alert alert-danger">
        <i class="fa-solid fa-triangle-exclamation me-2"></i>{{ error }}
        <br>Veuillez réessayer avec un autre moyen de paiement.
      </div>
      <div v-else-if="paymentStatus === PaymentStatusEnum.Failed" class="alert alert-danger">
        <i class="fa-solid fa-circle-xmark me-2"></i>
        Le paiement a échoué. Vous pouvez essayer avec un autre moyen de paiement.
      </div>

      <div v-if="paymentStatus === PaymentStatusEnum.Processing" class="alert alert-info">
        <i class="fa-solid fa-clock me-2"></i>
        Le paiement est en cours de traitement. Vérifiez son état avant de poursuivre.
      </div>

      <div v-if="paymentStatus === PaymentStatusEnum.Canceled" class="alert alert-warning">
        <i class="fa-solid fa-ban me-2"></i>
        Le paiement a été annulé. Vous pouvez lancer une nouvelle tentative.
      </div>

      <VueStripeProvider
        v-if="paymentStatus === PaymentStatusEnum.Pending && clientSecret && publishableKey"
        :publishable-key="publishableKey"
        locale="fr"
        @error="onStripeProviderError"
      >
        <VueStripeElements
          :client-secret="clientSecret"
          locale="fr"
          @error="onStripeElementsError"
        >
          <TravelStripePaymentForm
            ref="travelPaymentFormInstance"
            :client-secret="clientSecret"
            @ready="paymentElementReady = true"
            @change="paymentElementComplete = $event"
            @error="onStripeElementError"
          />
        </VueStripeElements>
      </VueStripeProvider>

      <div class="d-flex justify-content-center mt-3">
        <button
          v-if="paymentStatus === PaymentStatusEnum.Pending"
          class="btn btn-lg btn-primary"
          :disabled="processing || !paymentElementReady || !paymentElementComplete"
          @click="confirmPayment"
        >
          <span v-if="processing" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Régler {{ formatPriceWithoutPlusSign(amount) }}
          <i v-if="!processing" class="fa-solid fa-lock ms-2"></i>
        </button>
        <button
          v-else-if="paymentStatus === PaymentStatusEnum.Processing"
          class="btn btn-outline-primary"
          :disabled="synchronizing"
          @click="synchronizePayment"
        >
          <span v-if="synchronizing" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Vérifier le paiement
          <i v-if="!synchronizing" class="fa-solid fa-arrows-rotate ms-2"></i>
        </button>
        <button
          v-else
          class="btn btn-outline-primary"
          :disabled="loading"
          @click="initializePayment"
        >
          Réessayer
          <i class="fa-solid fa-rotate-right ms-2"></i>
        </button>
      </div>
    </template>

    <PageBottomMessage  message-key="pagePayment"></PageBottomMessage>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { VueStripeElements, VueStripeProvider } from '@vue-stripe/vue-stripe';
import travelApi from '@/api/travelApi';
import { formatPriceWithoutPlusSign } from '@/compositions/helper';
import TravelStripePaymentForm from '@/views/user/travel/TravelStripePaymentForm.vue';
import { useField } from 'vee-validate';

const participation = defineModel<UserParticipation>({ required: true });
const { setValue: setPaymentValue } = useField<UserParticipationPayment>(() => 'payment');

const loading = ref(true);
const processing = ref(false);
const synchronizing = ref(false);
const paymentElementReady = ref(false);
const paymentElementComplete = ref(false);
const error = ref<string | null>(null);
const amountInCents = ref<number | null>(participation.value.payment?.amountInCents ?? null);
const paymentStatus = ref<PaymentStatusEnum | null>(participation.value.payment?.status ?? null);
const clientSecret = ref<string | null>(null);
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const travelPaymentFormInstance = useTemplateRef<InstanceType<typeof TravelStripePaymentForm>>('travelPaymentFormInstance');

const amount = computed(() => {
  return amountInCents.value != null ? amountInCents.value / 100 : 0;
});

const setPaymentState = (status: PaymentStatusEnum, amount: number) => {
  paymentStatus.value = status;
  amountInCents.value = amount;
  setPaymentValue({
    status,
    amountInCents: amount
  });
};

const resetPaymentElementState = () => {
  clientSecret.value = null;
  paymentElementReady.value = false;
  paymentElementComplete.value = false;
};

const getErrorMessage = (caught: unknown) => {
  return caught instanceof Error ? caught.message : 'Une erreur est survenue. Veuillez réessayer.';
};

const onStripeProviderError = (stripeError: Error) => {
  error.value = stripeError.message;
};

const onStripeElementsError = (message: string) => {
  error.value = message;
};

const onStripeElementError = (message: string) => {
  error.value = message;
};

const hasStripeRedirectResult = () => {
  const parameters = new URLSearchParams(window.location.search);
  return parameters.has('payment_intent') || parameters.has('redirect_status');
};

const initializePayment = async () => {
  resetPaymentElementState();
  loading.value = true;
  processing.value = false;
  error.value = null;
  paymentStatus.value = null;

  try {
    const travelId = participation.value.travel.id;
    if (travelId == null) {
      throw new Error('Le voyage est introuvable.');
    }

    const response = await travelApi.createPaymentIntent(travelId);
    setPaymentState(response.data.status, response.data.amountInCents);

    if (hasStripeRedirectResult()) {
      const synchronization = await travelApi.synchronizePayment(travelId);
      setPaymentState(synchronization.data.status, synchronization.data.amountInCents);
    }

    if (paymentStatus.value !== PaymentStatusEnum.Pending) {
      return;
    }

    if (!publishableKey) {
      throw new Error('La clé publique Stripe n\'est pas configurée.');
    }

    clientSecret.value = response.data.clientSecret;
  } catch (caught: unknown) {
    resetPaymentElementState();
    paymentStatus.value = null;
    error.value = getErrorMessage(caught);
  } finally {
    loading.value = false;
  }
};

const synchronizePayment = async () => {
  synchronizing.value = true;
  error.value = null;

  try {
    const travelId = participation.value.travel.id;
    if (travelId == null) {
      throw new Error('Le voyage est introuvable.');
    }

    const response = await travelApi.synchronizePayment(travelId);
    setPaymentState(response.data.status, response.data.amountInCents);
    if (response.data.status !== PaymentStatusEnum.Pending) {
      resetPaymentElementState();
    }
  } catch (caught: unknown) {
    error.value = getErrorMessage(caught);
  } finally {
    synchronizing.value = false;
  }
};

const confirmPayment = async () => {
  if (!travelPaymentFormInstance.value) return;

  processing.value = true;
  error.value = null;

  try {
    const result = await travelPaymentFormInstance.value.confirmPayment();

    if (result.error) {
      await synchronizePayment();
      error.value = result.error.message ?? 'Le paiement n\'a pas pu être confirmé.';
      return;
    }

    await synchronizePayment();
  } catch (caught: unknown) {
    error.value = getErrorMessage(caught);
  } finally {
    processing.value = false;
  }
};

const isValidated = computed(() => paymentStatus.value === PaymentStatusEnum.Succeeded);
defineExpose({ isValidated });

onMounted(() => {
  initializePayment();
});
</script>
